import React, { useEffect, useMemo, useState } from 'react';
import { ClipboardCheck, Loader2, ListChecks } from 'lucide-react';
import api from '../lib/api';
import { useLang, tx } from '../i18n/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { useCategories } from '../hooks/useCategories';
import { hasAccess } from '../components/LockedDialog';
import { PageHero } from '../components/articles/ArticlesHero';
import { ArticleCategoryTiles } from '../components/articles/ArticleCategoryTiles';
import { QuizCard } from '../components/library/QuizCard';
import { QUIZZES_HERO_IMG } from '../components/articles/articleUtils';

export const useLevels = () => {
  const { lang } = useLang();
  const L = (ar, en) => tx(lang, ar, en);
  return [
    { value: 'beginner', ar: L('مبتدئ', 'Beginner'), en: L('مبتدئ', 'Beginner') },
    { value: 'intermediate', ar: L('متوسط', 'Intermediate'), en: L('متوسط', 'Intermediate') },
    { value: 'advanced', ar: L('متقدم', 'Advanced'), en: L('متقدم', 'Advanced') },
  ];
};

export default function Quizzes() {
  const { lang } = useLang();
  const L = (ar, en) => tx(lang, ar, en);
  const { user } = useAuth();
  const { getLabel } = useCategories();
  const levels = useLevels();
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [level, setLevel] = useState('all');
  const [draft, setDraft] = useState('');
  const [query, setQuery] = useState('');
  const canAccess = hasAccess(user);

  useEffect(() => {
    api.get('/quizzes').then(({ data }) => setQuizzes((data || []).map(q => ({ ...q, categories: [q.level] })))).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return quizzes.filter(x => (level === 'all' || x.level === level) &&
      (!q || [x.titleAr, x.titleEn, x.descAr, x.descEn].some(s => (s || '').toLowerCase().includes(q))));
  }, [quizzes, level, query]);

  return (
    <main data-testid="quizzes-page" className="page-y min-h-screen">
      <div className="site-container">
        <PageHero
          testId="quizzes-hero" image={QUIZZES_HERO_IMG} BadgeIcon={ClipboardCheck}
          badge={L('اختبارات تفاعلية', 'Interactive quizzes')}
          title={L('الاختبارات', 'Quizzes')}
          highlight={L('قِس مستواك', 'Measure your level')} rest={L('في الأتمتة الصناعية', 'in industrial automation')}
          desc={L('اختبارات قصيرة بمستويات مختلفة مع تقييم فوري وشرح لكل إجابة.', 'Short quizzes at different levels with instant scoring and an explanation for every answer.')}
          placeholder={L('ابحث عن اختبار ...', 'Search for a quiz ...')} searchLabel={L('بحث', 'Search')}
          query={draft} onQuery={(v) => { setDraft(v); if (!v) setQuery(''); }} onSubmit={() => setQuery(draft)} />
        <div className="pt-4">
          <ArticleCategoryTiles categories={levels} posts={quizzes} active={level} onChange={setLevel} testPrefix="quizzes-level" />
        </div>
        <div className="mb-6">
          <h2 className="flex items-center gap-2.5 text-xl sm:text-2xl font-black text-slate-900">
            <ListChecks className="w-7 h-7 text-emerald-500" />{L('جميع الاختبارات', 'All quizzes')}
          </h2>
          <p className="text-slate-500 text-sm mt-1">{L('اختر اختباراً وابدأ الآن', 'Pick a quiz and start now')}</p>
        </div>
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>
        ) : filtered.length === 0 ? (
          <div data-testid="quizzes-empty" className="text-center text-slate-500 py-16 bg-white rounded-2xl border border-dashed border-slate-200">{L('لا توجد اختبارات مطابقة', 'No matching quizzes')}</div>
        ) : (
          <div data-testid="quizzes-grid" className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filtered.map((q, i) => (
              <QuizCard key={q.id} quiz={q} index={i} getLabel={getLabel} locked={!canAccess && !q.is_free}
                levelLabel={(levels.find(l => l.value === q.level) || levels[0]).ar} />
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
